/**
 * `GET /reports/payroll-summary` — the payload, typed to what the handler
 * actually returns (api#794, `src/lib/payroll-summary.ts`).
 *
 * ── WHY THESE ARE HERE AND NOT DRAWN FROM `api-manifest.d.ts` ─────────────
 *
 * Until the api#804 re-mirror the manifest predated api#794 and recorded the
 * OLD handler's three keys (`period` / `staff` / `summary`), so typing the
 * screen through `ApiResponse<'GET /api/v1/reports/payroll-summary'>` would
 * have pinned it to a shape the server had stopped producing. The mirror now
 * records the new top-level keys (`operations` / `period_rule` / `range` /
 * `trucking`), but only the top level — the manifest records keys, not the
 * nested totals below — so these hand types remain the ones the screen reads.
 *
 * CLAUDE.md's rule for exactly this: type the interface to the ACTUAL payload.
 * These were read off the handler and its lib, not inferred from the screen.
 *
 * ── THE TWO THINGS A READER MUST NOT GET WRONG ───────────────────────────
 *
 * 1. THERE IS NO COMBINED TOTAL, ON PURPOSE. Operations and trucking are two
 *    payroll systems on two period calendars. The endpoint that preceded this
 *    one added them under one heading, so a tenant with both read a real
 *    figure plus a structural zero with nothing able to say which was which.
 *    Nothing in this file, and nothing rendering it, may add `operations` to
 *    `trucking`.
 *
 * 2. COMMITTED AND DRAFT ARE BOTH REAL MONEY AND ONLY ONE IS PAYABLE. TNT's
 *    all-time figure is $83,324.21 committed and $90,437.43 draft. A reader
 *    who takes `all` as payable is out by more than the payable half. Every
 *    surface showing `all` has to say what it is made of.
 */

/** A timesheet's money, summed. `row_count: 0` means no pay was generated — not $0.00. */
export interface PayrollMoneyTotals {
  row_count: number;
  earnings: number;
  overtime_earnings: number;
  adjustments: number;
  gross_pay: number;
}

/** Trucking entries carry no earnings/overtime/adjustments split, so its totals are gross-only. */
export interface TruckingGrossTotals {
  row_count: number;
  gross_pay: number;
}

export interface PayrollTotalsSplit<T> {
  /** Timesheet status `approved` or `exported` — money the office has committed to. */
  committed: T;
  /** Status `draft` or `active` — real money, not yet committed. */
  draft: T;
  /** Summed from every row, not from the two halves above. */
  all: T;
}

export interface PayrollPeriod {
  id: string;
  period_number?: number | null;
  period_start: string;
  period_end: string;
  pay_date?: string | null;
  status: string;
}

export interface OperationsPayrollRow {
  pay_period_id: string;
  period_start: string;
  period_end: string;
  user_id: string;
  /** NULL when both name columns are empty — `users` has three nullable name columns. */
  name: string | null;
  role: string | null;
  timesheet_status: string;
  committed: boolean;
  total_hours: number | null;
  overtime_hours: number | null;
  /* The four money keys are COALESCEd to 0 in SQL, so a row's figure is a real
     number and a real zero is a real zero. Absence lives one level up, in
     `row_count: 0` on the totals. */
  earnings: number;
  overtime_earnings: number;
  adjustments: number;
  gross_pay: number;
}

export interface TruckingPayrollRow {
  pay_period_id: string;
  period_start: string;
  period_end: string;
  period_status: string;
  committed: boolean;
  driver_id: string;
  name: string | null;
  regular_hours: number;
  ot_hours: number;
  double_ot_hours: number;
  gross_pay: number;
}

export interface OperationsPayrollSection {
  source: 'employee_timesheets';
  periods: PayrollPeriod[];
  rows: OperationsPayrollRow[];
  worker_count: number;
  totals: PayrollTotalsSplit<PayrollMoneyTotals>;
}

export interface TruckingPayrollSection {
  source: 'trucking_payroll_entries';
  periods: PayrollPeriod[];
  rows: TruckingPayrollRow[];
  driver_count: number;
  totals: PayrollTotalsSplit<TruckingGrossTotals>;
}

export interface PayrollSummaryReportData {
  range: { start_date: string; end_date: string };
  period_rule: 'period_end_in_range';
  operations: OperationsPayrollSection;
  /**
   * `null` for a tenant without the `trucking` vertical — which is every
   * tenant today. NOT an empty section: the endpoint did not look, so the
   * screen has nothing to report rather than "no trucking pay". Render the
   * section only when this is present.
   */
  trucking: TruckingPayrollSection | null;
}

/**
 * A half's gross, or `null` when that half has no rows.
 *
 * `row_count === 0` is the only spelling of "no pay was generated here". The
 * money key beside it is a summed 0, and a summed 0 over nothing is not $0.00
 * — it is a statement nobody made. This is the one place that turns the pair
 * into the em-dash rule's `null`, so no call site has to remember to.
 *
 * Typed on the two keys it reads rather than on either totals interface, so it
 * serves `PayrollMoneyTotals` and the gross-only `TruckingGrossTotals` without
 * a second copy — the two halves must not word an absent figure two ways.
 */
export function payrollGross(totals: { row_count: number; gross_pay: number }): number | null {
  return totals.row_count === 0 ? null : totals.gross_pay;
}
